import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, ListGroup, Spinner, Alert, Badge } from "react-bootstrap";
import { FiShield, FiTrash2, FiRefreshCw } from "react-icons/fi";
import { getHost } from "../utils/utils";

const getToken = () => localStorage.getItem("jwt");

const MonitorBlacklistModal = ({ show, onHide, monitor }) => {
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [message, setMessage] = useState("");

  const fetchBlacklist = useCallback(async () => {
    if (!monitor?._id) return;
    setLoading(true);
    setMessage("");
    try {
      const res = await fetch(
        `${getHost()}/api/admin/monitors/${monitor._id}/blacklist`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setBlacklist(data.blacklisted || []);
    } catch (error) {
      setMessage(`Failed to load blacklist: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [monitor]);

  useEffect(() => {
    if (show && monitor?._id) fetchBlacklist();
  }, [show, monitor, fetchBlacklist]);

  const unblock = async (pid) => {
    if (!monitor?._id) return;
    if (!window.confirm(
      `Unblock ${pid}? If this monitor discovers products from categories or searches, it can pick ${pid} up again and ping it. To stop tracking a product for good, delete it from the products page instead.`,
    )) return;
    setRemoving(pid);
    setMessage("");
    try {
      const res = await fetch(
        `${getHost()}/api/admin/monitors/${monitor._id}/blacklist?pid=${encodeURIComponent(pid)}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setBlacklist((prev) => prev.filter((p) => p !== pid));
      setMessage(`Unblocked ${pid}`);
    } catch (error) {
      setMessage(`Failed to unblock ${pid}: ${error.message}`);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="bg-dark text-light">
      <Modal.Header closeButton closeVariant="white" className="border-secondary">
        <Modal.Title className="d-flex align-items-center gap-2 min-w-0">
          <FiShield className="flex-shrink-0" />
          Blacklisted PIDs
          {monitor?.name && <Badge bg="secondary" className="ms-2 text-truncate" style={{ maxWidth: "40vw" }}>{monitor.name}</Badge>}
          {!loading && <Badge bg="info" className="ms-1">{blacklist.length}</Badge>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted small mb-3">
          These PIDs are blocked at the monitor level — the monitor will skip them entirely (no fetching, no pings). Deleting a product on the products page no longer adds it here: deleted products live in that page's Deleted view. Unblock to let the monitor track the PID again.
        </p>

        {message && (
          <Alert variant={message.startsWith("Failed") ? "danger" : "success"} className="py-2 px-3 small">
            {message}
          </Alert>
        )}

        <div className="d-flex justify-content-end mb-2">
          <Button size="sm" variant="outline-secondary" onClick={fetchBlacklist} disabled={loading}>
            <FiRefreshCw className={loading ? "spin" : ""} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-4"><Spinner size="sm" /> Loading…</div>
        ) : blacklist.length === 0 ? (
          <div className="text-muted text-center py-4">No blacklisted PIDs on this monitor.</div>
        ) : (
          <ListGroup variant="flush" style={{ maxHeight: 400, overflowY: "auto" }}>
            {blacklist.map((pid) => (
              <ListGroup.Item
                key={pid}
                className="bg-transparent text-light border-secondary d-flex align-items-center justify-content-between gap-2"
              >
                <code className="text-info text-break min-w-0">{pid}</code>
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="flex-shrink-0"
                  onClick={() => unblock(pid)}
                  disabled={removing === pid}
                >
                  {removing === pid ? <Spinner size="sm" /> : <><FiTrash2 /> Unblock</>}
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>

      <Modal.Footer className="border-secondary">
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MonitorBlacklistModal;
